import { directive, Directive } from 'lit/directive.js';
import { LitElement, css, nothing, html } from 'lit';
import { state, query, property, customElement } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate$1(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata$1(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-beta.3',
    };
}

class AriaKeyboardListController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    setConfig(config) {
        this.config = config;
    }
    hostDisconnected() {
        this.host.removeController(this);
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    handleKeyDown(event) {
        if (!this.config)
            return;
        const items = this.config.getItems();
        const currentIndex = items.indexOf(this.getActiveElement());
        switch (event.key) {
            case 'ArrowDown':
                // case 'ArrowRight':
                event.preventDefault();
                if (items.length && currentIndex < items.length - 1)
                    this.config.setActive(currentIndex < 0 ? 0 : currentIndex + 1);
                break;
            case 'ArrowUp':
                // case 'ArrowLeft':
                event.preventDefault();
                if (items.length)
                    this.config.setActive(currentIndex < 0 ? items.length - 1 : currentIndex - 1);
                break;
            case 'Home':
                event.preventDefault();
                if (items.length)
                    this.config.setActive(0);
                break;
            case 'End':
                event.preventDefault();
                if (items.length)
                    this.config.setActive(items.length - 1);
                break;
            case 'Escape':
                event.preventDefault();
                this.config.closeMenu();
                break;
        }
    }
}

var styles$1 = css`.dropdown,
.dropdown-menu {
  --bsi-dropdown-animation-speed: var(--bsi-transition-instant);
  --bsi-dropdown-background: var(--bsi-color-background-inverse);
  --bsi-dropdown-border-color: transparent;
  --bsi-dropdown-border-radius: 2px;
  --bsi-dropdown-border-width: 0;
  --bsi-dropdown-box-shadow: var(--bsi-elevation-low);
  --bsi-dropdown-color: var(--bsi-color-text-base);
  --bsi-dropdown-divider-bg: var(--bsi-color-border-subtle);
  --bsi-dropdown-divider-margin-y: var(--bsi-spacing-xxs);
  --bsi-dropdown-font-size: var(--bsi-label-font-size);
  --bsi-dropdown-header-color: var(--bsi-color-text-secondary);
  --bsi-dropdown-header-font-size: var(--bsi-label-font-size);
  --bsi-dropdown-header-padding-x: var(--bsi-spacing-s);
  --bsi-dropdown-header-padding-y: var(--bsi-spacing-xxs);
  --bsi-dropdown-inner-border-radius: var(--bsi-radius-smooth);
  --bsi-dropdown-item-padding-x: var(--bsi-spacing-s);
  --bsi-dropdown-item-padding-y: var(--bsi-spacing-xs);
  --bsi-dropdown-link-active-color: var(--bsi-color-text-primary-active);
  --bsi-dropdown-link-color: var(--bsi-color-text-primary);
  --bsi-dropdown-link-hover-color: var(--bsi-color-text-primary-hover);
  --bsi-dropdown-min-width: 10rem;
  --bsi-dropdown-notch-base-size: 1.125rem;
  --bsi-dropdown-notch-position-x: 20px;
  --bsi-dropdown-notch-position-y: -8px;
  --bsi-dropdown-padding-x: 0;
  --bsi-dropdown-padding-y: var(--bsi-spacing-xxs);
  --bsi-dropdown-spacer: 0.125rem;
  --bsi-dropdown-vertical-shift: 10px;
  --bsi-dropdown-zindex: 8;
}

.dropup,
.dropend,
.dropdown,
.dropstart,
.dropup-center,
.dropdown-center {
  position: relative;
}

.dropdown-toggle {
  white-space: nowrap;
}
.dropdown-toggle::after {
  display: inline-block;
  margin-left: 0.255em;
  vertical-align: 0.255em;
  content: "";
  border-top: 0.3em solid;
  border-right: 0.3em solid transparent;
  border-bottom: 0;
  border-left: 0.3em solid transparent;
}
.dropdown-toggle:empty::after {
  margin-left: 0;
}

.dropdown-menu {
  position: absolute;
  z-index: var(--bsi-dropdown-zindex);
  display: none;
  min-width: var(--bsi-dropdown-min-width);
  margin: 0;
  padding: var(--bsi-dropdown-padding-y) var(--bsi-dropdown-padding-x);
  border: var(--bsi-dropdown-border-width) solid var(--bsi-dropdown-border-color);
  border-radius: var(--bsi-dropdown-border-radius);
  background-color: var(--bsi-dropdown-background);
  color: var(--bsi-dropdown-color);
  list-style: none;
  text-align: left;
  background-clip: padding-box;
  font-size: var(--bsi-dropdown-font-size);
  box-shadow: var(--bsi-dropdown-box-shadow);
}
.dropdown-menu[data-bs-popper] {
  top: 100%;
  left: 0;
  margin-top: var(--bsi-dropdown-spacer);
}
.dropdown-menu.full-width {
  width: 100%;
}
.dropdown-menu.full-width .link-list li {
  display: inline-block;
  width: auto;
}
.dropdown-menu.full-width .link-list li:hover, .dropdown-menu.full-width .link-list li:focus {
  background: none;
  text-decoration: underline;
}
.dropdown-menu .link-list {
  margin-bottom: 0;
}
.dropdown-menu:before {
  position: absolute;
  top: var(--bsi-dropdown-notch-position-y);
  left: var(--bsi-dropdown-notch-position-x);
  content: "";
  width: var(--bsi-dropdown-notch-base-size);
  height: var(--bsi-dropdown-notch-base-size);
  border-radius: var(--bsi-dropdown-border-radius);
  background-color: var(--bsi-dropdown-background);
  transform: rotate(45deg);
}
.dropdown-menu[data-popper-placement=top-start] {
  border-radius: var(--bsi-dropdown-border-radius) var(--bsi-dropdown-border-radius) 0 0;
}
.dropdown-menu[data-popper-placement=top-start]:before {
  top: auto;
  bottom: var(--bsi-dropdown-notch-position-y);
}
.dropdown-menu[data-popper-placement=bottom-end]:before {
  right: calc(var(--bsi-dropdown-notch-position-x) / 2);
  left: auto;
}
.dropdown-menu.dark {
  --bsi-dropdown-text-color: var(--bsi-color-text-inverse);
  --bsi-dropdown-background: var(--bsi-color-background-primary-muted);
  --bsi-dropdown-link-color: var(--bsi-color-text-inverse);
  --bsi-dropdown-divider-bg: var(--bsi-color-border-subtle);
  --bsi-dropdown-link-active-color: var(--bsi-color-text-inverse);
  --bsi-dropdown-header-color: var(--bsi-color-text-inverse);
}
.dropdown-menu.dark:before {
  background-color: var(--bsi-dropdown-background);
}
.dropdown-menu.dark .link-list-heading {
  color: var(--bsi-color-text-inverse);
}
.dropdown-menu.dark .link-list span.divider {
  background: #2e465e;
}
.dropdown-menu.dark .link-list a span,
.dropdown-menu.dark .link-list a:hover span {
  color: var(--bsi-color-text-inverse);
}
.dropdown-menu.dark .link-list a.active span {
  text-decoration: underline;
}
.dropdown-menu.dark .link-list a.disabled span {
  color: hsla(var(--bsi-color-text-base), 0.35);
  cursor: not-allowed;
}
.dropdown-menu.show {
  display: block;
}
.dropdown-menu.show[data-popper-placement=bottom-start] {
  animation: dropdownFadeIn forwards var(--bsi-dropdown-animation-speed);
}
.dropdown-menu.show[data-popper-placement=top-start] {
  animation: dropdownFadeInTop forwards var(--bsi-dropdown-animation-speed);
}
.dropdown-menu.show[data-popper-placement=left-start] {
  animation: dropdownFadeInTop forwards var(--bsi-dropdown-animation-speed);
}
.dropdown-menu.show[data-popper-placement=left-start]:before {
  top: 20px;
  right: -6px;
  left: auto;
}
.dropdown-menu.show[data-popper-placement=right-start] {
  animation: dropdownFadeInTop forwards var(--bsi-dropdown-animation-speed);
}
.dropdown-menu.show[data-popper-placement=right-start]:before {
  top: 20px;
  left: -6px;
}

.dropdown-menu-start {
  --bsi-dropdown-position: start;
}
.dropdown-menu-start[data-bs-popper] {
  right: auto;
  left: 0;
}

.dropdown-menu-end {
  --bsi-dropdown-position: end;
}
.dropdown-menu-end[data-bs-popper] {
  right: 0;
  left: auto;
}

@media (min-width: 576px) {
  .dropdown-menu-sm-start {
    --bsi-dropdown-position: start;
  }
  .dropdown-menu-sm-start[data-bs-popper] {
    right: auto;
    left: 0;
  }
  .dropdown-menu-sm-end {
    --bsi-dropdown-position: end;
  }
  .dropdown-menu-sm-end[data-bs-popper] {
    right: 0;
    left: auto;
  }
}
@media (min-width: 768px) {
  .dropdown-menu-md-start {
    --bsi-dropdown-position: start;
  }
  .dropdown-menu-md-start[data-bs-popper] {
    right: auto;
    left: 0;
  }
  .dropdown-menu-md-end {
    --bsi-dropdown-position: end;
  }
  .dropdown-menu-md-end[data-bs-popper] {
    right: 0;
    left: auto;
  }
}
@media (min-width: 992px) {
  .dropdown-menu-lg-start {
    --bsi-dropdown-position: start;
  }
  .dropdown-menu-lg-start[data-bs-popper] {
    right: auto;
    left: 0;
  }
  .dropdown-menu-lg-end {
    --bsi-dropdown-position: end;
  }
  .dropdown-menu-lg-end[data-bs-popper] {
    right: 0;
    left: auto;
  }
}
@media (min-width: 1200px) {
  .dropdown-menu-xl-start {
    --bsi-dropdown-position: start;
  }
  .dropdown-menu-xl-start[data-bs-popper] {
    right: auto;
    left: 0;
  }
  .dropdown-menu-xl-end {
    --bsi-dropdown-position: end;
  }
  .dropdown-menu-xl-end[data-bs-popper] {
    right: 0;
    left: auto;
  }
}
@media (min-width: 1400px) {
  .dropdown-menu-xxl-start {
    --bsi-dropdown-position: start;
  }
  .dropdown-menu-xxl-start[data-bs-popper] {
    right: auto;
    left: 0;
  }
  .dropdown-menu-xxl-end {
    --bsi-dropdown-position: end;
  }
  .dropdown-menu-xxl-end[data-bs-popper] {
    right: 0;
    left: auto;
  }
}
.dropup .dropdown-menu[data-bs-popper] {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: var(--bsi-dropdown-spacer);
}
.dropup .dropdown-toggle::after {
  display: inline-block;
  margin-left: 0.255em;
  vertical-align: 0.255em;
  content: "";
  border-top: 0;
  border-right: 0.3em solid transparent;
  border-bottom: 0.3em solid;
  border-left: 0.3em solid transparent;
}
.dropup .dropdown-toggle:empty::after {
  margin-left: 0;
}

.dropend .dropdown-menu[data-bs-popper] {
  top: 0;
  right: auto;
  left: 100%;
  margin-top: 0;
  margin-left: var(--bsi-dropdown-spacer);
}
.dropend .dropdown-toggle::after {
  display: inline-block;
  margin-left: 0.255em;
  vertical-align: 0.255em;
  content: "";
  border-top: 0.3em solid transparent;
  border-right: 0;
  border-bottom: 0.3em solid transparent;
  border-left: 0.3em solid;
}
.dropend .dropdown-toggle:empty::after {
  margin-left: 0;
}
.dropend .dropdown-toggle::after {
  vertical-align: 0;
}

.dropstart .dropdown-menu[data-bs-popper] {
  top: 0;
  right: 100%;
  left: auto;
  margin-top: 0;
  margin-right: var(--bsi-dropdown-spacer);
}
.dropstart .dropdown-toggle::after {
  display: inline-block;
  margin-left: 0.255em;
  vertical-align: 0.255em;
  content: "";
}
.dropstart .dropdown-toggle::after {
  display: none;
}
.dropstart .dropdown-toggle::before {
  display: inline-block;
  margin-right: 0.255em;
  vertical-align: 0.255em;
  content: "";
  border-top: 0.3em solid transparent;
  border-right: 0.3em solid;
  border-bottom: 0.3em solid transparent;
}
.dropstart .dropdown-toggle:empty::after {
  margin-left: 0;
}
.dropstart .dropdown-toggle::before {
  vertical-align: 0;
}

.dropdown-divider {
  height: 0;
  margin: var(--bsi-dropdown-divider-margin-y) 0;
  overflow: hidden;
  border-top: 1px solid var(--bsi-dropdown-divider-bg);
  opacity: 1;
}

.dropdown-item.list-item {
  width: auto;
  padding: var(--bsi-dropdown-item-padding-y) var(--bsi-dropdown-item-padding-x);
  border: 0;
  background-color: transparent;
  text-align: inherit;
  text-decoration: none;
  white-space: nowrap;
}
.dropdown-item.list-item:hover, .dropdown-item.list-item:focus {
  text-decoration: none;
}

.dropdown-header.link-list-heading {
  display: block;
  margin-bottom: 0;
  padding: var(--bsi-dropdown-header-padding-y) var(--bsi-dropdown-header-padding-x);
  color: var(--bsi-dropdown-header-color);
  font-size: var(--bsi-dropdown-header-font-size);
  white-space: nowrap;
}

.dropdown-item-text {
  display: block;
  padding: var(--bsi-dropdown-item-padding-y) var(--bsi-dropdown-item-padding-x);
  color: var(--bsi-dropdown-link-color);
}

@keyframes dropdownFadeIn {
  0% {
    margin-top: 0;
    opacity: 0;
  }
  100% {
    margin-top: var(--bsi-dropdown-vertical-shift);
    opacity: 1;
  }
}
@keyframes dropdownFadeInTop {
  0% {
    margin-top: 0;
    opacity: 0;
  }
  100% {
    margin-top: calc(-1 * var(--bsi-dropdown-vertical-shift));
    opacity: 1;
  }
}
.dropdown.dropup .dropdown-toggle:after,
.btn-group.dropup .dropdown-toggle:after {
  display: none;
}
.dropdown.dropup .dropdown-toggle[aria-expanded=true] .icon-expand,
.btn-group.dropup .dropdown-toggle[aria-expanded=true] .icon-expand {
  transform: scaleY(1);
}
.dropdown.dropup .dropdown-toggle .icon-expand,
.btn-group.dropup .dropdown-toggle .icon-expand {
  transform: scaleY(-1);
}
.dropdown.dropend .dropdown-toggle:after,
.btn-group.dropend .dropdown-toggle:after {
  display: none;
}
.dropdown.dropend .dropdown-toggle[aria-expanded=true] .icon-expand,
.btn-group.dropend .dropdown-toggle[aria-expanded=true] .icon-expand {
  transform: rotate(90deg);
}
.dropdown.dropend .dropdown-toggle .icon-expand,
.btn-group.dropend .dropdown-toggle .icon-expand {
  transform: rotate(-90deg);
}
.dropdown.dropstart .dropdown-toggle:before,
.btn-group.dropstart .dropdown-toggle:before {
  display: none;
}
.dropdown.dropstart .dropdown-toggle[aria-expanded=true] .icon-expand,
.btn-group.dropstart .dropdown-toggle[aria-expanded=true] .icon-expand {
  transform: rotate(-90deg);
}
.dropdown.dropstart .dropdown-toggle .icon-expand,
.btn-group.dropstart .dropdown-toggle .icon-expand {
  transform: rotate(90deg);
}

.btn-dropdown {
  --bsi-dropdown-button-color: var(--bsi-color-text-primary);
  --bsi-dropdown-button-padding: 10px;
  --bsi-dropdown-button-background: #fff;
  border-radius: 0;
  box-shadow: none;
  color: var(--bsi-dropdown-button-color);
}
@media (min-width: 992px) {
  .btn-dropdown {
    font-size: var(--bsi-label-font-size-l);
  }
}
.btn-dropdown:hover {
  --bsi-dropdown-button-color: var(--bsi-color-link-hover);
  text-decoration: underline;
}
.btn-dropdown:not(:disabled):active {
  box-shadow: none;
}

.btn-dropdown:after,
.dropdown-toggle:after {
  content: "";
  display: inline;
  width: auto;
  height: auto;
  margin: 0;
  border: none;
  vertical-align: bottom;
}
.btn-dropdown .icon-expand,
.dropdown-toggle .icon-expand {
  transition: transform var(--bsi-dropdown-animation-speed);
}
.btn-dropdown .icon.icon-xs,
.dropdown-toggle .icon.icon-xs {
  transform: translateY(-1px);
  transition: transform var(--bsi-dropdown-animation-speed);
}
.btn-dropdown[aria-expanded=true] .icon-expand,
.dropdown-toggle[aria-expanded=true] .icon-expand {
  transform: scaleY(-1);
}

.link-list-wrapper {
  --bsi-linklist-divider-size: 1px;
  --bsi-linklist-heading-color: var(--bsi-color-text-secondary);
  --bsi-linklist-heading-font-size: var(--bsi-heading-6-font-size);
  --bsi-linklist-item-font-size: var(--bsi-label-font-size);
  --bsi-linklist-item-line-height: var(--bsi-label-leading);
  --bsi-linklist-item-spacing: var(--bsi-spacing-xxs);
  --bsi-linklist-nested-spacing: var(--bsi-spacing-s);
}

/* stylelint-disable-next-line no-duplicate-selectors */
.link-list-wrapper .link-list-heading,
.link-list-wrapper h3 {
  margin-bottom: var(--bsi-spacing-xxs);
  color: var(--bsi-linklist-heading-color);
  font-size: var(--bsi-linklist-heading-font-size);
  font-weight: var(--bsi-heading-font-weight-weak);
  line-height: var(--bsi-heading-line-height);
}
.link-list-wrapper h3 {
  padding: 0 var(--bsi-spacing-m);
}
.link-list-wrapper ul {
  padding: 0;
  list-style-type: none;
}
.link-list-wrapper ul.link-sublist {
  padding-left: var(--bsi-linklist-nested-spacing);
}
.link-list-wrapper li span,
.link-list-wrapper .list-item {
  font-size: var(--bsi-linklist-item-font-size);
  line-height: var(--bsi-linklist-item-line-height);
}
.link-list-wrapper .list-item {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 1;
  padding-top: var(--bsi-linklist-item-spacing);
  padding-bottom: var(--bsi-linklist-item-spacing);
  text-decoration: none;
}
.link-list-wrapper .list-item.active span, .link-list-wrapper .list-item:hover:not(.disable) span {
  text-decoration: underline;
}
.link-list-wrapper .list-item.active span {
  color: var(--bsi-color-link-active);
}
.link-list-wrapper .list-item.active .icon {
  color: var(--bsi-color-icon-primary-active);
}
.link-list-wrapper .list-item:hover:not(.disabled) span {
  color: var(--bsi-color-link-hover);
}
.link-list-wrapper .list-item:hover:not(.disabled) .icon {
  fill: var(--bsi-icon-primary-hover);
}
.link-list-wrapper .list-item:hover:not(.disabled) .icon.icon-inverse {
  fill: var(--bsi-icon-inverse);
}
.link-list-wrapper .list-item.disabled {
  cursor: not-allowed;
}
.link-list-wrapper .list-item.disabled:hover span {
  text-decoration: none;
}
.link-list-wrapper .list-item.disabled span {
  color: var(--bsi-color-text-disabled);
}
.link-list-wrapper .list-item.disabled svg {
  fill: var(--bsi-icon-disabled);
}
.link-list-wrapper .list-item.disabled svg.secondary {
  fill: var(--bsi-icon-disabled);
}
.link-list-wrapper .list-item.medium,
.link-list-wrapper .list-item .list-item-title {
  font-weight: var(--bsi-font-weight-solid);
}
.link-list-wrapper .list-item.large {
  --bsi-linklist-item-font-size: var(--bsi-label-font-size-l);
}
.link-list-wrapper .list-item.avatar {
  display: flex;
  margin-bottom: var(--bsi-spacing-xxs);
}
.link-list-wrapper .list-item.avatar .avatar {
  display: block;
  margin-right: var(--bsi-spacing-s);
}
.link-list-wrapper .list-item.icon-right, .link-list-wrapper .list-item.icon-left {
  padding-right: 0;
  padding-left: 0;
}
.link-list-wrapper .list-item.icon-left .icon {
  left: 0;
  flex-shrink: 0;
  margin-right: var(--bsi-spacing-xxs);
}
.link-list-wrapper .list-item.icon-left p {
  width: 100%;
}
.link-list-wrapper .list-item.icon-right {
  width: 100%;
}
.link-list-wrapper .list-item.icon-right .list-item-title-icon-wrapper {
  justify-content: space-between;
}
.link-list-wrapper .list-item.right-icon .list-item-title-icon-wrapper {
  justify-content: space-between;
}
.link-list-wrapper .list-item.right-icon .icon {
  transition: transform var(--bsi-transition-instant);
}
.link-list-wrapper .list-item.right-icon .icon.secondary {
  color: var(--bsi-icon-secondary);
}
.link-list-wrapper .list-item[data-bs-toggle] .icon {
  transition: transform 0.3s;
}
.link-list-wrapper .list-item[aria-expanded=true] .icon {
  transform: rotate(180deg);
}
.link-list-wrapper .list-item .icon {
  display: inline-block;
  flex-shrink: 0;
}
.link-list-wrapper .list-item .list-item-title-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
}
.link-list-wrapper .list-item .list-item-title-icon-wrapper .list-item-title {
  margin-right: var(--bsi-spacing-xxs);
}
.link-list-wrapper .list-item > p {
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-caption-font-size);
  line-height: var(--bsi-caption-leading);
}
.link-list-wrapper .list-item > span {
  display: inline-block;
  color: var(--bsi-color-link);
}
.link-list-wrapper .divider {
  display: block;
  height: var(--bsi-linklist-divider-size);
  margin: var(--bsi-spacing-xxs) 0;
  background: var(--bsi-color-border-subtle);
}
.link-list-wrapper .form-check {
  padding: 0 var(--bsi-spacing-m);
}

.multiline .link-list li:not(:last-child) {
  border-bottom: 1px solid var(--bsi-color-border-subtle);
}
.multiline .list-item-title-icon-wrapper {
  margin-bottom: var(--bsi-spacing-3xs);
}
.multiline .list-item.icon-right .list-item-title-icon-wrapper {
  margin-right: 0;
}
.multiline .list-item-title {
  margin-bottom: var(--bsi-spacing-xxs);
  font-size: var(--bsi-label-font-size);
}
@media (min-width: 992px) {
  .multiline .list-item-title {
    font-size: var(--bsi-label-font-size-l);
  }
}
.multiline .list-item {
  --bsi-linklist-item-spacing: var(--bsi-spacing-s);
  display: block;
}
.multiline p {
  margin-bottom: 0;
}

.dark,
.theme-dark {
  --bsi-linklist-heading-color: var(--bsi-color-text-inverse);
}
.dark .link-list-wrapper .list-item:not(.disabled) span,
.dark .link-list-wrapper .list-item:hover span,
.theme-dark .link-list-wrapper .list-item:not(.disabled) span,
.theme-dark .link-list-wrapper .list-item:hover span {
  color: var(--bsi-color-text-inverse);
}
.dark .link-list-wrapper .list-item.disabled,
.dark .link-list-wrapper .list-item.disabled span,
.theme-dark .link-list-wrapper .list-item.disabled,
.theme-dark .link-list-wrapper .list-item.disabled span {
  color: hsla(var(--bsi-color-text-base), 0.35);
}

.dropdown-menu {
  z-index: 11;
}
.dropdown-menu::before {
  content: none;
}
.dropdown-menu.dark {
  --bsi-dropdown-header-color: var(--bsi-color-text-inverse);
  --bsi-color-border-subtle: #2e465e;
}
.dropdown-menu .arrow {
  z-index: -1;
  width: var(--bsi-dropdown-notch-base-size);
  height: var(--bsi-dropdown-notch-base-size);
  border-radius: var(--bsi-dropdown-border-radius);
  background-color: var(--bsi-dropdown-background);
}

.link-list {
  margin-top: 0;
}

:host {
  position: relative;
  display: inline-block;
  --bsi-dropdown-animation-speed: 0.3s;
  --bsi-icon-default: currentColor;
}

it-button,
it-popover {
  display: inline-flex;
}

it-popover[placement=right-start] .dropdown-menu,
it-popover[placement=right] .dropdown-menu,
it-popover[placement=right-end] .dropdown-menu,
it-popover[placement=left-start] .dropdown-menu,
it-popover[placement=left] .dropdown-menu,
it-popover[placement=left-end] .dropdown-menu {
  border-radius: var(--bsi-dropdown-border-radius);
}

it-popover[placement^=bottom] .dropdown-menu.show {
  animation: dropdownFadeIn forwards var(--bsi-dropdown-animation-speed);
}
it-popover[placement^=top] .dropdown-menu.show, it-popover[placement^=left] .dropdown-menu.show, it-popover[placement^=right] .dropdown-menu.show {
  animation: dropdownFadeInTop forwards var(--bsi-dropdown-animation-speed);
}

.dropdown-toggle .icon-expand.top {
  transform: scaleY(-1);
}
.dropdown-toggle .icon-expand.left {
  transform: rotate(90deg);
}
.dropdown-toggle .icon-expand.right {
  transform: rotate(-90deg);
}
.dropdown-toggle[it-aria-expanded] .icon-expand {
  transform: scaleY(-1);
}
.dropdown-toggle[it-aria-expanded] .icon-expand.top {
  transform: scaleY(1);
}
.dropdown-toggle[it-aria-expanded] .icon-expand.left {
  transform: rotate(-90deg);
}
.dropdown-toggle[it-aria-expanded] .icon-expand.right {
  transform: rotate(90deg);
}`;

// Base class without @customElement decorator for inheritance
class ItDropdownBase extends BaseComponent {
    constructor() {
        super(...arguments);
        this.disabled = false;
        this.variant = 'primary';
        this.alignment = 'bottom-start';
        this.dark = false;
        this.fullWidth = false;
        this.itRole = 'list';
        this.itAriaLabel = '';
        this.offset = null;
        this.noFlip = false;
        /** When true, hides the expand (caret) icon next to the trigger label. Used e.g. by the avatar "+N" group balloon. */
        this.hideExpandIcon = false;
        /** When true, centres the popover arrow on the trigger instead of the fixed notch position. Used by avatar dropdowns. */
        this.centerArrow = false;
        this._popoverOpen = false;
        this._buttonId = this.generateId('it-dropdown');
        this._menuId = this.generateId('it-dropdown-menu');
        this._ariaNav = new AriaKeyboardListController(this);
        this._onTriggerClick = () => {
            if (this.disabled)
                return;
            this._popoverOpen = !this._popoverOpen;
        };
        this._onPopoverOpen = () => {
            this._popoverOpen = true;
            this.setAttribute('data-it-aria-expanded', 'true');
        };
        this._onPopoverClose = () => {
            this._popoverOpen = false;
            this.setAttribute('data-it-aria-expanded', 'false');
        };
        this._onTabKeyDown = (event, items, active, currentIndex) => {
            function isTrigger(el) {
                return currentIndex === -1;
            }
            if (event.key === 'Tab') {
                if (isTrigger()) {
                    if (event.shiftKey)
                        this._popoverOpen = false;
                    return;
                }
                // if (event.shiftKey && currentIndex === -1) {
                //   // from the trigger item
                //   this._popoverOpen = false;
                // }
                if (!event.shiftKey && currentIndex === items.length - 1) {
                    // from the last item
                    this._popoverOpen = false;
                }
                if (active.ariaDisabled) {
                    // as of the day of this implementation, tabbing through disabled items doesn't work natively
                    // maybe because of some web components behavior
                    if (event.shiftKey) {
                        this._ariaNav.handleKeyDown(new KeyboardEvent('keydown', { ...event, key: 'ArrowUp' }));
                    }
                    else {
                        this._ariaNav.handleKeyDown(new KeyboardEvent('keydown', { ...event, key: 'ArrowDown' }));
                    }
                    if (!active.href)
                        event.preventDefault();
                }
                if (!active.href) {
                    // when the dropdown items don't have an href they are not natively tabbable, handle manually
                    if (event.shiftKey && currentIndex > 0) {
                        event.preventDefault();
                        this._ariaNav.handleKeyDown(new KeyboardEvent('keydown', { ...event, key: 'ArrowUp' }));
                    }
                    if (!event.shiftKey && currentIndex < items.length - 1) {
                        event.preventDefault();
                        this._ariaNav.handleKeyDown(new KeyboardEvent('keydown', { ...event, key: 'ArrowDown' }));
                    }
                    if (!event.shiftKey && currentIndex === items.length - 1) {
                        this._triggerEl?.dispatchEvent(new KeyboardEvent('keydown', { ...event }));
                    }
                }
            }
        };
        this._onKeyDown = (event) => {
            const items = this._menuItems.map((item) => item.getFocusableElement()).filter((el) => !!el);
            const active = this.getActiveElement();
            if (!active)
                return;
            const currentIndex = items.indexOf(active);
            // handle Tab keydown separately to anable overriding this bhv from consumer components that extend this.
            this._onTabKeyDown(event, items, active, currentIndex);
            const handle = () => {
                this._ariaNav.setConfig({
                    getItems: () => items,
                    setActive: (idx) => {
                        items[idx]?.focus();
                    },
                    closeMenu: () => {
                        this.addEventListener('it-popover-close', () => {
                            this._triggerEl?.focus();
                        }, { once: true, capture: true });
                        this._popoverOpen = false;
                    },
                    trigger: this._triggerEl,
                });
                this._ariaNav.handleKeyDown(event);
            };
            if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
                if (!this.disabled && !this._popoverOpen && currentIndex === -1) {
                    this.addEventListener('it-popover-open', handle, { once: true });
                    this._popoverOpen = true;
                    return;
                }
            }
            handle();
        };
        this._onDocumentClick = (event) => {
            // Use composedPath() instead of event.target: when this dropdown is nested inside
            // another shadow DOM host (e.g. it-toolbar-item), the target seen at the document
            // level is retargeted to that outer host, so `this.contains(event.target)` would
            // always be false and immediately close the dropdown right after opening it.
            if (this._popoverOpen && !event.composedPath().includes(this)) {
                this._popoverOpen = false;
            }
        };
    }
    get _triggerEl() {
        return this.shadowRoot?.getElementById(this._buttonId) ?? null;
    }
    connectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.connectedCallback();
        // Ascolta i click fuori dal componente per chiudere il popover
        document.addEventListener('click', this._onDocumentClick, true);
    }
    disconnectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.disconnectedCallback();
        document.removeEventListener('click', this._onDocumentClick, true);
    }
    get _menuItems() {
        if (!this._slotEl)
            return [];
        return this._slotEl
            .assignedElements({ flatten: true })
            .filter((el) => el.tagName === 'IT-DROPDOWN-ITEM');
    }
    _setChildrenProperties() {
        for (const item of this._menuItems) {
            item.dark = this.dark;
            item.fullWidth = this.fullWidth;
            if (this.itRole === 'menu')
                item.itRole = 'menuitem';
            else if (this.itRole === 'listbox')
                item.itRole = 'option';
            else if (this.itRole === 'tree')
                item.itRole = 'treeitem';
            else if (this.itRole === 'list') {
                // set role to 'listitem' to fix axe tools alert, and force itRole to 'presentation' to avoid duplicate roles in the megamenu
                item.itRole = 'presentation';
                item.role = 'listitem';
            }
            else
                item.itRole = undefined;
        }
    }
    updated() {
        this._setChildrenProperties();
    }
    // https://github.com/primefaces/primeng/issues/14851 for conditional aria controls
    render() {
        return html `
      <it-popover
        placement=${this.alignment}
        @it-popover-open=${this._onPopoverOpen}
        @it-popover-close=${this._onPopoverClose}
        exportparts="focusable, icon, button, it-icon, it-button, dropdown-button, dropdown-icon-expand, popover-content"
        part="dropdown-popover"
        ?open=${this._popoverOpen}
        offset=${ifDefined(this.offset || undefined)}
        cross-axis-offset=${this.alignment.startsWith('left') || this.alignment.startsWith('right') ? '10' : '0'}
        ?no-flip=${this.noFlip}
        ?center-arrow=${this.centerArrow}
        controlled
      >
        <it-button
          id=${this._buttonId}
          slot="trigger"
          ?disabled="${this.disabled}"
          type="button"
          variant=${ifDefined(this.variant)}
          size=${ifDefined(this.size)}
          @click=${this._onTriggerClick}
          @keydown=${{ handleEvent: this._onKeyDown, capture: true }}
          class="dropdown-toggle"
          it-aria-label=${ifDefined(this.itAriaLabel ? this.itAriaLabel : undefined)}
          exportparts="focusable, button, icon, it-icon"
          part="dropdown-button"
          it-aria-haspopup="${this.itRole === 'list' ? 'true' : this.itRole}"
          it-aria-controls=${this._popoverOpen ? this._menuId : nothing}
        >
          ${this.alignment.startsWith('left') && !this.hideExpandIcon
            ? html `<it-icon
                name="it-expand"
                class="icon-expand left"
                size="sm"
                exportparts="icon"
                part="dropdown-icon-expand"
              ></it-icon>`
            : ''}

          <slot name="label">${this.label}</slot>
          ${!this.alignment.startsWith('left') && !this.hideExpandIcon
            ? html `<it-icon
                name="it-expand"
                class=${this.composeClass('icon-expand', {
                right: this.alignment.startsWith('right'),
                top: this.alignment.startsWith('top'),
            })}
                exportparts="icon"
                part="dropdown-icon-expand"
                size="sm"
              ></it-icon>`
            : ''}
        </it-button>
        <div
          slot="content"
          part="popover-content"
          class="${this.composeClass('dropdown-menu', {
            show: this._popoverOpen,
            dark: this.dark,
            'full-width': this.fullWidth,
        })}"
          aria-labelledby=${this._buttonId}
        >
          <div class="link-list-wrapper">
            <slot name="header"></slot>
            <ul
              id=${this._menuId}
              class="link-list"
              role=${ifDefined(this.itRole !== 'list' ? this.itRole : undefined)}
              @keydown=${{ handleEvent: this._onKeyDown, capture: true }}
              aria-orientation=${ifDefined(this.fullWidth ? 'horizontal' : undefined)}
            >
              <slot @slotchange=${this._setChildrenProperties}></slot>
            </ul>
          </div>
        </div>
      </it-popover>
    `;
    }
}
ItDropdownBase.styles = styles$1;
ItDropdownBase.shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
};
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItDropdownBase.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItDropdownBase.prototype, "disabled", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItDropdownBase.prototype, "size", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItDropdownBase.prototype, "variant", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItDropdownBase.prototype, "alignment", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], ItDropdownBase.prototype, "dark", void 0);
__decorate([
    property({ type: Boolean, attribute: 'full-width', reflect: true }),
    __metadata("design:type", Object)
], ItDropdownBase.prototype, "fullWidth", void 0);
__decorate([
    property({ type: String, attribute: 'it-role' }),
    __metadata("design:type", String)
], ItDropdownBase.prototype, "itRole", void 0);
__decorate([
    property({ type: String, attribute: 'it-aria-label' }),
    __metadata("design:type", String)
], ItDropdownBase.prototype, "itAriaLabel", void 0);
__decorate([
    property({ type: Number, attribute: 'offset' }),
    __metadata("design:type", Object)
], ItDropdownBase.prototype, "offset", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-flip' }),
    __metadata("design:type", Boolean)
], ItDropdownBase.prototype, "noFlip", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-expand-icon', reflect: true }),
    __metadata("design:type", Boolean)
], ItDropdownBase.prototype, "hideExpandIcon", void 0);
__decorate([
    property({ type: Boolean, attribute: 'center-arrow', reflect: true }),
    __metadata("design:type", Boolean)
], ItDropdownBase.prototype, "centerArrow", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItDropdownBase.prototype, "_popoverOpen", void 0);
__decorate([
    query('slot:not([name])'),
    __metadata("design:type", HTMLSlotElement)
], ItDropdownBase.prototype, "_slotEl", void 0);

var styles = css`.row {
  --bsi-gutter-x: 24px;
  --bsi-gutter-y: 0;
  display: flex;
  flex-wrap: wrap;
  margin-top: calc(-1 * var(--bsi-gutter-y));
  margin-right: calc(-0.5 * var(--bsi-gutter-x));
  margin-left: calc(-0.5 * var(--bsi-gutter-x));
}
.row > * {
  flex-shrink: 0;
  width: 100%;
  max-width: 100%;
  padding-right: calc(var(--bsi-gutter-x) * 0.5);
  padding-left: calc(var(--bsi-gutter-x) * 0.5);
  margin-top: var(--bsi-gutter-y);
}

.col {
  flex: 1 0 0%;
}

.row-cols-auto > * {
  flex: 0 0 auto;
  width: auto;
}

.row-cols-1 > * {
  flex: 0 0 auto;
  width: 100%;
}

.row-cols-2 > * {
  flex: 0 0 auto;
  width: 50%;
}

.row-cols-3 > * {
  flex: 0 0 auto;
  width: 33.3333333333%;
}

.row-cols-4 > * {
  flex: 0 0 auto;
  width: 25%;
}

.row-cols-5 > * {
  flex: 0 0 auto;
  width: 20%;
}

.row-cols-6 > * {
  flex: 0 0 auto;
  width: 16.6666666667%;
}

.col-auto {
  flex: 0 0 auto;
  width: auto;
}

.col-1 {
  flex: 0 0 auto;
  width: 8.33333333%;
}

.col-2 {
  flex: 0 0 auto;
  width: 16.66666667%;
}

.col-3 {
  flex: 0 0 auto;
  width: 25%;
}

.col-4 {
  flex: 0 0 auto;
  width: 33.33333333%;
}

.col-5 {
  flex: 0 0 auto;
  width: 41.66666667%;
}

.col-6 {
  flex: 0 0 auto;
  width: 50%;
}

.col-7 {
  flex: 0 0 auto;
  width: 58.33333333%;
}

.col-8 {
  flex: 0 0 auto;
  width: 66.66666667%;
}

.col-9 {
  flex: 0 0 auto;
  width: 75%;
}

.col-10 {
  flex: 0 0 auto;
  width: 83.33333333%;
}

.col-11 {
  flex: 0 0 auto;
  width: 91.66666667%;
}

.col-12 {
  flex: 0 0 auto;
  width: 100%;
}

.offset-1 {
  margin-left: 8.33333333%;
}

.offset-2 {
  margin-left: 16.66666667%;
}

.offset-3 {
  margin-left: 25%;
}

.offset-4 {
  margin-left: 33.33333333%;
}

.offset-5 {
  margin-left: 41.66666667%;
}

.offset-6 {
  margin-left: 50%;
}

.offset-7 {
  margin-left: 58.33333333%;
}

.offset-8 {
  margin-left: 66.66666667%;
}

.offset-9 {
  margin-left: 75%;
}

.offset-10 {
  margin-left: 83.33333333%;
}

.offset-11 {
  margin-left: 91.66666667%;
}

.g-0,
.gx-0 {
  --bsi-gutter-x: 0;
}

.g-0,
.gy-0 {
  --bsi-gutter-y: 0;
}

.g-1,
.gx-1 {
  --bsi-gutter-x: 0.25rem;
}

.g-1,
.gy-1 {
  --bsi-gutter-y: 0.25rem;
}

.g-2,
.gx-2 {
  --bsi-gutter-x: 0.5rem;
}

.g-2,
.gy-2 {
  --bsi-gutter-y: 0.5rem;
}

.g-3,
.gx-3 {
  --bsi-gutter-x: 1rem;
}

.g-3,
.gy-3 {
  --bsi-gutter-y: 1rem;
}

.g-4,
.gx-4 {
  --bsi-gutter-x: 1.5rem;
}

.g-4,
.gy-4 {
  --bsi-gutter-y: 1.5rem;
}

.g-5,
.gx-5 {
  --bsi-gutter-x: 3rem;
}

.g-5,
.gy-5 {
  --bsi-gutter-y: 3rem;
}

@media (min-width: 576px) {
  .col-sm {
    flex: 1 0 0%;
  }
  .row-cols-sm-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-sm-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-sm-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-sm-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-sm-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-sm-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-sm-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-sm-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-sm-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-sm-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-sm-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-sm-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-sm-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-sm-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-sm-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-sm-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-sm-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-sm-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-sm-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-sm-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-sm-0 {
    margin-left: 0;
  }
  .offset-sm-1 {
    margin-left: 8.33333333%;
  }
  .offset-sm-2 {
    margin-left: 16.66666667%;
  }
  .offset-sm-3 {
    margin-left: 25%;
  }
  .offset-sm-4 {
    margin-left: 33.33333333%;
  }
  .offset-sm-5 {
    margin-left: 41.66666667%;
  }
  .offset-sm-6 {
    margin-left: 50%;
  }
  .offset-sm-7 {
    margin-left: 58.33333333%;
  }
  .offset-sm-8 {
    margin-left: 66.66666667%;
  }
  .offset-sm-9 {
    margin-left: 75%;
  }
  .offset-sm-10 {
    margin-left: 83.33333333%;
  }
  .offset-sm-11 {
    margin-left: 91.66666667%;
  }
  .g-sm-0,
  .gx-sm-0 {
    --bsi-gutter-x: 0;
  }
  .g-sm-0,
  .gy-sm-0 {
    --bsi-gutter-y: 0;
  }
  .g-sm-1,
  .gx-sm-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-sm-1,
  .gy-sm-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-sm-2,
  .gx-sm-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-sm-2,
  .gy-sm-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-sm-3,
  .gx-sm-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-sm-3,
  .gy-sm-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-sm-4,
  .gx-sm-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-sm-4,
  .gy-sm-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-sm-5,
  .gx-sm-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-sm-5,
  .gy-sm-5 {
    --bsi-gutter-y: 3rem;
  }
}
@media (min-width: 768px) {
  .col-md {
    flex: 1 0 0%;
  }
  .row-cols-md-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-md-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-md-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-md-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-md-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-md-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-md-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-md-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-md-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-md-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-md-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-md-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-md-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-md-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-md-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-md-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-md-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-md-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-md-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-md-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-md-0 {
    margin-left: 0;
  }
  .offset-md-1 {
    margin-left: 8.33333333%;
  }
  .offset-md-2 {
    margin-left: 16.66666667%;
  }
  .offset-md-3 {
    margin-left: 25%;
  }
  .offset-md-4 {
    margin-left: 33.33333333%;
  }
  .offset-md-5 {
    margin-left: 41.66666667%;
  }
  .offset-md-6 {
    margin-left: 50%;
  }
  .offset-md-7 {
    margin-left: 58.33333333%;
  }
  .offset-md-8 {
    margin-left: 66.66666667%;
  }
  .offset-md-9 {
    margin-left: 75%;
  }
  .offset-md-10 {
    margin-left: 83.33333333%;
  }
  .offset-md-11 {
    margin-left: 91.66666667%;
  }
  .g-md-0,
  .gx-md-0 {
    --bsi-gutter-x: 0;
  }
  .g-md-0,
  .gy-md-0 {
    --bsi-gutter-y: 0;
  }
  .g-md-1,
  .gx-md-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-md-1,
  .gy-md-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-md-2,
  .gx-md-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-md-2,
  .gy-md-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-md-3,
  .gx-md-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-md-3,
  .gy-md-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-md-4,
  .gx-md-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-md-4,
  .gy-md-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-md-5,
  .gx-md-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-md-5,
  .gy-md-5 {
    --bsi-gutter-y: 3rem;
  }
}
@media (min-width: 992px) {
  .col-lg {
    flex: 1 0 0%;
  }
  .row-cols-lg-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-lg-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-lg-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-lg-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-lg-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-lg-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-lg-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-lg-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-lg-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-lg-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-lg-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-lg-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-lg-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-lg-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-lg-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-lg-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-lg-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-lg-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-lg-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-lg-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-lg-0 {
    margin-left: 0;
  }
  .offset-lg-1 {
    margin-left: 8.33333333%;
  }
  .offset-lg-2 {
    margin-left: 16.66666667%;
  }
  .offset-lg-3 {
    margin-left: 25%;
  }
  .offset-lg-4 {
    margin-left: 33.33333333%;
  }
  .offset-lg-5 {
    margin-left: 41.66666667%;
  }
  .offset-lg-6 {
    margin-left: 50%;
  }
  .offset-lg-7 {
    margin-left: 58.33333333%;
  }
  .offset-lg-8 {
    margin-left: 66.66666667%;
  }
  .offset-lg-9 {
    margin-left: 75%;
  }
  .offset-lg-10 {
    margin-left: 83.33333333%;
  }
  .offset-lg-11 {
    margin-left: 91.66666667%;
  }
  .g-lg-0,
  .gx-lg-0 {
    --bsi-gutter-x: 0;
  }
  .g-lg-0,
  .gy-lg-0 {
    --bsi-gutter-y: 0;
  }
  .g-lg-1,
  .gx-lg-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-lg-1,
  .gy-lg-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-lg-2,
  .gx-lg-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-lg-2,
  .gy-lg-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-lg-3,
  .gx-lg-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-lg-3,
  .gy-lg-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-lg-4,
  .gx-lg-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-lg-4,
  .gy-lg-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-lg-5,
  .gx-lg-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-lg-5,
  .gy-lg-5 {
    --bsi-gutter-y: 3rem;
  }
}
@media (min-width: 1200px) {
  .col-xl {
    flex: 1 0 0%;
  }
  .row-cols-xl-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-xl-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-xl-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-xl-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-xl-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-xl-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-xl-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-xl-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-xl-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-xl-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-xl-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-xl-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-xl-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-xl-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-xl-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-xl-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-xl-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-xl-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-xl-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-xl-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-xl-0 {
    margin-left: 0;
  }
  .offset-xl-1 {
    margin-left: 8.33333333%;
  }
  .offset-xl-2 {
    margin-left: 16.66666667%;
  }
  .offset-xl-3 {
    margin-left: 25%;
  }
  .offset-xl-4 {
    margin-left: 33.33333333%;
  }
  .offset-xl-5 {
    margin-left: 41.66666667%;
  }
  .offset-xl-6 {
    margin-left: 50%;
  }
  .offset-xl-7 {
    margin-left: 58.33333333%;
  }
  .offset-xl-8 {
    margin-left: 66.66666667%;
  }
  .offset-xl-9 {
    margin-left: 75%;
  }
  .offset-xl-10 {
    margin-left: 83.33333333%;
  }
  .offset-xl-11 {
    margin-left: 91.66666667%;
  }
  .g-xl-0,
  .gx-xl-0 {
    --bsi-gutter-x: 0;
  }
  .g-xl-0,
  .gy-xl-0 {
    --bsi-gutter-y: 0;
  }
  .g-xl-1,
  .gx-xl-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-xl-1,
  .gy-xl-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-xl-2,
  .gx-xl-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-xl-2,
  .gy-xl-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-xl-3,
  .gx-xl-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-xl-3,
  .gy-xl-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-xl-4,
  .gx-xl-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-xl-4,
  .gy-xl-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-xl-5,
  .gx-xl-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-xl-5,
  .gy-xl-5 {
    --bsi-gutter-y: 3rem;
  }
}
@media (min-width: 1400px) {
  .col-xxl {
    flex: 1 0 0%;
  }
  .row-cols-xxl-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-xxl-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-xxl-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-xxl-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-xxl-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-xxl-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-xxl-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-xxl-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-xxl-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-xxl-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-xxl-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-xxl-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-xxl-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-xxl-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-xxl-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-xxl-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-xxl-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-xxl-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-xxl-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-xxl-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-xxl-0 {
    margin-left: 0;
  }
  .offset-xxl-1 {
    margin-left: 8.33333333%;
  }
  .offset-xxl-2 {
    margin-left: 16.66666667%;
  }
  .offset-xxl-3 {
    margin-left: 25%;
  }
  .offset-xxl-4 {
    margin-left: 33.33333333%;
  }
  .offset-xxl-5 {
    margin-left: 41.66666667%;
  }
  .offset-xxl-6 {
    margin-left: 50%;
  }
  .offset-xxl-7 {
    margin-left: 58.33333333%;
  }
  .offset-xxl-8 {
    margin-left: 66.66666667%;
  }
  .offset-xxl-9 {
    margin-left: 75%;
  }
  .offset-xxl-10 {
    margin-left: 83.33333333%;
  }
  .offset-xxl-11 {
    margin-left: 91.66666667%;
  }
  .g-xxl-0,
  .gx-xxl-0 {
    --bsi-gutter-x: 0;
  }
  .g-xxl-0,
  .gy-xxl-0 {
    --bsi-gutter-y: 0;
  }
  .g-xxl-1,
  .gx-xxl-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-xxl-1,
  .gy-xxl-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-xxl-2,
  .gx-xxl-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-xxl-2,
  .gy-xxl-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-xxl-3,
  .gx-xxl-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-xxl-3,
  .gy-xxl-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-xxl-4,
  .gx-xxl-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-xxl-4,
  .gy-xxl-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-xxl-5,
  .gx-xxl-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-xxl-5,
  .gy-xxl-5 {
    --bsi-gutter-y: 3rem;
  }
}
.row.variable-gutters {
  margin-right: -12px;
  margin-left: -12px;
}
.row.variable-gutters > .col,
.row.variable-gutters > [class*=col-] {
  padding-right: 12px;
  padding-left: 12px;
}
.row.variable-gutters {
  margin-right: -6px;
  margin-left: -6px;
}
.row.variable-gutters > .col,
.row.variable-gutters > [class*=col-] {
  padding-right: 6px;
  padding-left: 6px;
}
@media (min-width: 576px) {
  .row.variable-gutters {
    margin-right: -6px;
    margin-left: -6px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 6px;
    padding-left: 6px;
  }
}
@media (min-width: 768px) {
  .row.variable-gutters {
    margin-right: -10px;
    margin-left: -10px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 10px;
    padding-left: 10px;
  }
}
@media (min-width: 992px) {
  .row.variable-gutters {
    margin-right: -12px;
    margin-left: -12px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 12px;
    padding-left: 12px;
  }
}
@media (min-width: 1200px) {
  .row.variable-gutters {
    margin-right: -12px;
    margin-left: -12px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 12px;
    padding-left: 12px;
  }
}
@media (min-width: 1400px) {
  .row.variable-gutters {
    margin-right: -14px;
    margin-left: -14px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 14px;
    padding-left: 14px;
  }
}

.row.row-column-border > [class^=col-] {
  padding-top: var(--bsi-spacing-l);
  padding-bottom: var(--bsi-spacing-l);
  border-top: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
}
.row.row-column-border > [class^=col-]:first-child {
  border: none;
}
.row.row-column-border > [class^=col-] .navbar {
  padding: 0;
}
.row.row-column-border > [class^=col-] .navbar .menu-wrapper .nav-link {
  padding-right: 0;
}
.row.row-column-menu-left > [class^=col-]:first-child {
  padding: var(--bsi-spacing-s) 0;
}
.row.row-column-menu-right > [class^=col-]:last-child {
  padding: var(--bsi-spacing-s) 0;
}
.row.row-card {
  background-color: var(--bsi-color-background-inverse);
}
@media (min-width: 992px) {
  .row.row-column-border {
    margin-top: 1rem;
    border-top: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  }
  .row.row-column-border > [class^=col-] {
    padding: 3rem 3rem;
    border-top: none;
    border-left: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  }
  .row.row-column-border > [class^=col-]:first-child {
    border: none;
    padding-left: 0;
  }
  .row.row-column-border > [class^=col-] .navbar {
    padding: 8px 0;
  }
  .row.row-column-border > [class^=col-] .navbar .menu-wrapper {
    padding: 0;
  }
  .row.row-column-menu-left > [class^=col-]:first-child {
    padding: 0;
  }
  .row.row-column-menu-right > [class^=col-]:last-child {
    padding: 0;
  }
}
.row.row-full-width {
  max-width: 100vw;
  margin: 0 calc(-50vw + 50%);
}
.row.row-full-width > * {
  width: 100%;
  display: flex;
  flex-direction: column;
}
.row.row-full-width > * img {
  width: 100%;
  height: 100%;
  max-height: 600px;
  object-fit: cover;
}
.row.row-title {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
@media (min-width: 576px) {
  .row.row-title {
    flex-direction: row;
    align-items: flex-start;
  }
}
.row.row-border h1 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h2 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h3 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h4 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h5 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h6 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
@media (min-width: 576px) {
  .row.row-border {
    border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
    padding-bottom: var(--bsi-spacing-s);
    margin-bottom: var(--bsi-spacing-s);
  }
  .row.row-border h1 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h2 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h3 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h4 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h5 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h6 {
    border: none;
    margin: 0;
    padding: 0;
  }
}
.row.row-calendar {
  display: block;
}

.sticky-wrapper.is-sticky {
  position: fixed;
}
.sticky-wrapper.is-sticky.navbar-wrapper {
  z-index: 1;
  left: 0;
  right: 0;
  width: auto;
}
.sticky-wrapper.is-sticky.navbar-wrapper .navbar {
  padding-top: var(--bsi-spacing-s);
  padding-bottom: var(--bsi-spacing-s);
  border-top: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
}
.sticky-wrapper.is-sticky.navbar-wrapper.sticky-expanded {
  z-index: auto;
}
@media (min-width: 992px) {
  .sticky-wrapper.is-sticky.navbar-wrapper {
    z-index: auto;
    left: auto;
    right: auto;
    width: unset;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper .navbar {
    border: none;
    background-color: transparent;
    padding: 0;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper .navbar .menu-wrapper {
    padding: 0;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper .navbar .menu-wrapper .nav-link {
    padding-right: 0;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper .navbar.it-bottom-navscroll {
    border: none;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper.at-bottom {
    position: absolute;
    top: auto !important;
    bottom: 0;
  }
}
.sticky-wrapper.navbar-wrapper .navbar.it-top-navscroll, .sticky-wrapper.navbar-wrapper .navbar.it-bottom-navscroll {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  bottom: auto;
}

.megamenu {
  --bsi-megamenu-background-color: var(--bsi-color-background-inverse);
  --bsi-megamenu-content-spacing: var(--bsi-spacing-xs);
  --bsi-megamenu-content-border-color: var(--bsi-color-border-subtle);
}

/* stylelint-disable-next-line no-duplicate-selectors */
.megamenu .megamenu-content {
  padding: var(--bsi-megamenu-content-spacing);
}
.megamenu .it-description {
  background: transparent;
}
@media (max-width: 991.98px) {
  .megamenu .it-description {
    padding-top: var(--bsi-megamenu-content-spacing);
    padding-right: calc(var(--bsi-megamenu-content-spacing) * 2);
    padding-left: calc(var(--bsi-megamenu-content-spacing) * 2);
  }
}
.megamenu .it-description .description-content {
  max-width: 480px;
}
.megamenu .it-description .description-content img {
  display: block;
  width: 100%;
  margin-bottom: var(--bsi-spacing-s);
}
.megamenu .it-description .description-content p {
  font-size: var(--bsi-label-font-size);
  line-height: var(--bsi-body-line-height);
}
.megamenu .it-heading-link-wrapper,
.megamenu .it-footer-link-wrapper,
.megamenu .it-footer-link-wrapper-vertical {
  border-color: var(--bsi-megamenu-content-border-color);
}
.megamenu .it-heading-link-wrapper {
  margin-bottom: var(--bsi-spacing-s);
  padding-bottom: var(--bsi-spacing-s);
  border-bottom-width: 1px;
  border-bottom-style: solid;
}
.megamenu .it-heading-link,
.megamenu .it-footer-link {
  font-size: var(--bsi-heading-6-font-size);
  font-weight: var(--bsi-font-weight-solid);
  line-height: var(--bsi-heading-leading);
  text-decoration: none;
}
.megamenu .it-heading-link:hover, .megamenu .it-heading-link:focus,
.megamenu .it-footer-link:hover,
.megamenu .it-footer-link:focus {
  text-decoration: underline;
}
.megamenu .it-footer-link-wrapper,
.megamenu .it-footer-link-wrapper-vertical {
  margin-top: var(--bsi-spacing-m);
  margin-right: var(--bsi-spacing-m);
  margin-left: var(--bsi-spacing-m);
  padding-top: var(--bsi-spacing-m);
  border-top-width: 1px;
  border-top-style: solid;
}
.megamenu .it-footer-link {
  margin-bottom: var(--bsi-spacing-m);
}
.megamenu .it-footer-link:last-child {
  margin-bottom: 0;
}
.megamenu .dropdown-menu {
  --bsi-dropdown-item-padding-x: var(--bsi-spacing-xxs);
  --bsi-dropdown-item-padding-y: var(--bsi-spacing-xxs);
  --bsi-dropdown-spacer: 0;
  width: 100%;
  background: var(--bsi-megamenu-background-color);
}
@media (min-width: 992px) {
  .megamenu .dropdown-menu {
    --bsi-dropdown-padding-y: var(--bsi-spacing-xs);
    --bsi-dropdown-padding-x: var(--bsi-spacing-xs);
  }
}
.megamenu .link-list-wrapper {
  margin-bottom: 0;
}
.megamenu .link-list-wrapper .link-list {
  width: 100%;
}
.megamenu .link-list-wrapper .link-list .list-item {
  display: flex;
  align-items: center;
  white-space: normal;
}
.megamenu .link-list-wrapper .link-list .list-item span {
  display: inline-flex;
  align-items: center;
}

@media (min-width: 992px) {
  .navbar-nav .megamenu a.dropdown-toggle:before,
  .navbar-nav .megamenu button.dropdown-toggle:before {
    z-index: 2;
  }
  .navbar-nav .megamenu .dropdown-menu {
    z-index: 1;
  }
  .megamenu-content .col-12:last-child {
    padding-bottom: 0;
  }
  .megamenu-content .col-12 .link-list-wrapper {
    min-height: 100%;
  }
  .megamenu-content .col-12 .link-list-wrapper ul.link-list {
    display: flex;
    flex-direction: column;
  }
  .megamenu-content .col-12:first-child .link-list-wrapper {
    margin-bottom: 0;
  }
  .megamenu-content .it-vertical .link-list-wrapper ul.link-list {
    width: 100%;
  }
  .megamenu-content .it-vertical .link-list-wrapper ul.link-list li {
    margin-bottom: 3px;
  }
  .megamenu-content .it-vertical .link-list-wrapper ul.link-list li a {
    display: flex;
    justify-content: space-between;
  }
  .megamenu-content .it-vertical .link-list-wrapper ul.link-list li a span {
    font-weight: 600;
  }
  .megamenu-content .it-heading-link-wrapper {
    margin: 0 0 var(--bsi-megamenu-content-spacing);
    padding-bottom: var(--bsi-megamenu-content-spacing);
  }
  .megamenu-content .it-footer-link-wrapper-vertical {
    height: 100%;
    margin-top: 0;
    padding: 0;
    padding-left: var(--bsi-spacing-m);
    border-top: none;
    border-left: 1px solid var(--bsi-megamenu-content-border-color);
  }
  .megamenu-content .it-footer-link-wrapper-vertical .d-flex {
    height: 100%;
  }
  .megamenu-content .it-footer-link-wrapper .it-footer-link:not(:last-child) {
    margin-right: var(--bsi-megamenu-content-spacing);
  }
  .nav-item.megamenu {
    position: static;
  }
  .nav-item.megamenu > a.dropdown-toggle,
  .nav-item.megamenu > button.dropdown-toggle {
    position: relative;
  }
  .nav-item.megamenu > a.dropdown-toggle:before,
  .nav-item.megamenu > button.dropdown-toggle:before {
    position: absolute;
    top: auto;
    bottom: -14px;
    left: 24px;
    content: "";
    display: block;
    width: 18px;
    height: 18px;
    opacity: 0;
    border-radius: 2px;
    background-color: var(--bsi-color-background-inverse);
    transform: rotate(45deg);
    transition: opacity calc(var(0.3s) * 0.5) cubic-bezier(0.1, 0.57, 0.36, 0.99);
  }
  .nav-item.megamenu > button.dropdown-toggle:before {
    bottom: calc(var(6px) * -1 - 8);
  }
  .nav-item.megamenu a.dropdown-toggle.show:before,
  .nav-item.megamenu button.dropdown-toggle.show:before {
    opacity: 1;
  }
  .it-header-sticky.is-sticky .nav-item button.nav-link {
    font-size: 16px;
  }
}
@media (min-width: 1200px) {
  .it-header-sticky.is-sticky .nav-item button.nav-link {
    font-size: 18px;
  }
}
.p-0 {
  padding: 0 !important;
}

.p-1 {
  padding: 0.25rem !important;
}

.p-2 {
  padding: 0.5rem !important;
}

.p-3 {
  padding: 1rem !important;
}

.p-4 {
  padding: 1.5rem !important;
}

.p-5 {
  padding: 3rem !important;
}

.py-0 {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.py-1 {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}

.py-2 {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}

.py-3 {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}

.py-4 {
  padding-top: 1.5rem !important;
  padding-bottom: 1.5rem !important;
}

.py-5 {
  padding-top: 3rem !important;
  padding-bottom: 3rem !important;
}

.pt-0 {
  padding-top: 0 !important;
}

.pt-1 {
  padding-top: 0.25rem !important;
}

.pt-2 {
  padding-top: 0.5rem !important;
}

.pt-3 {
  padding-top: 1rem !important;
}

.pt-4 {
  padding-top: 1.5rem !important;
}

.pt-5 {
  padding-top: 3rem !important;
}

.pb-0 {
  padding-bottom: 0 !important;
}

.pb-1 {
  padding-bottom: 0.25rem !important;
}

.pb-2 {
  padding-bottom: 0.5rem !important;
}

.pb-3 {
  padding-bottom: 1rem !important;
}

.pb-4 {
  padding-bottom: 1.5rem !important;
}

.pb-5 {
  padding-bottom: 3rem !important;
}

.px-0 {
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.px-1 {
  padding-right: 0.25rem !important;
  padding-left: 0.25rem !important;
}

.px-2 {
  padding-right: 0.5rem !important;
  padding-left: 0.5rem !important;
}

.px-3 {
  padding-right: 1rem !important;
  padding-left: 1rem !important;
}

.px-4 {
  padding-right: 1.5rem !important;
  padding-left: 1.5rem !important;
}

.px-5 {
  padding-right: 3rem !important;
  padding-left: 3rem !important;
}

.ps-0 {
  padding-left: 0 !important;
}

.ps-1 {
  padding-left: 0.25rem !important;
}

.ps-2 {
  padding-left: 0.5rem !important;
}

.ps-3 {
  padding-left: 1rem !important;
}

.ps-4 {
  padding-left: 1.5rem !important;
}

.ps-5 {
  padding-left: 3rem !important;
}

.ms-0 {
  margin-left: 0 !important;
}

.ms-1 {
  margin-left: 0.25rem !important;
}

.ms-2 {
  margin-left: 0.5rem !important;
}

.ms-3 {
  margin-left: 1rem !important;
}

.ms-4 {
  margin-left: 1.5rem !important;
}

.ms-5 {
  margin-left: 3rem !important;
}

.ms-auto {
  margin-left: auto !important;
}

.me-0 {
  margin-right: 0 !important;
}

.me-1 {
  margin-right: 0.25rem !important;
}

.me-2 {
  margin-right: 0.5rem !important;
}

.me-3 {
  margin-right: 1rem !important;
}

.me-4 {
  margin-right: 1.5rem !important;
}

.me-5 {
  margin-right: 3rem !important;
}

.me-auto {
  margin-right: auto !important;
}

.text-end {
  text-align: end !important;
}

@media (min-width: 576px) {
  .p-sm-0 {
    padding: 0 !important;
  }
  .p-sm-1 {
    padding: 0.25rem !important;
  }
  .p-sm-2 {
    padding: 0.5rem !important;
  }
  .p-sm-3 {
    padding: 1rem !important;
  }
  .p-sm-4 {
    padding: 1.5rem !important;
  }
  .p-sm-5 {
    padding: 3rem !important;
  }
  .py-sm-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-sm-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-sm-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-sm-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-sm-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-sm-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-sm-0 {
    padding-top: 0 !important;
  }
  .pt-sm-1 {
    padding-top: 0.25rem !important;
  }
  .pt-sm-2 {
    padding-top: 0.5rem !important;
  }
  .pt-sm-3 {
    padding-top: 1rem !important;
  }
  .pt-sm-4 {
    padding-top: 1.5rem !important;
  }
  .pt-sm-5 {
    padding-top: 3rem !important;
  }
  .pb-sm-0 {
    padding-bottom: 0 !important;
  }
  .pb-sm-1 {
    padding-bottom: 0.25rem !important;
  }
  .pb-sm-2 {
    padding-bottom: 0.5rem !important;
  }
  .pb-sm-3 {
    padding-bottom: 1rem !important;
  }
  .pb-sm-4 {
    padding-bottom: 1.5rem !important;
  }
  .pb-sm-5 {
    padding-bottom: 3rem !important;
  }
  .px-sm-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-sm-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-sm-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-sm-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-sm-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-sm-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .ps-sm-0 {
    padding-left: 0 !important;
  }
  .ps-sm-1 {
    padding-left: 0.25rem !important;
  }
  .ps-sm-2 {
    padding-left: 0.5rem !important;
  }
  .ps-sm-3 {
    padding-left: 1rem !important;
  }
  .ps-sm-4 {
    padding-left: 1.5rem !important;
  }
  .ps-sm-5 {
    padding-left: 3rem !important;
  }
  .ms-sm-0 {
    margin-left: 0 !important;
  }
  .ms-sm-1 {
    margin-left: 0.25rem !important;
  }
  .ms-sm-2 {
    margin-left: 0.5rem !important;
  }
  .ms-sm-3 {
    margin-left: 1rem !important;
  }
  .ms-sm-4 {
    margin-left: 1.5rem !important;
  }
  .ms-sm-5 {
    margin-left: 3rem !important;
  }
  .ms-sm-auto {
    margin-left: auto !important;
  }
  .me-sm-0 {
    margin-right: 0 !important;
  }
  .me-sm-1 {
    margin-right: 0.25rem !important;
  }
  .me-sm-2 {
    margin-right: 0.5rem !important;
  }
  .me-sm-3 {
    margin-right: 1rem !important;
  }
  .me-sm-4 {
    margin-right: 1.5rem !important;
  }
  .me-sm-5 {
    margin-right: 3rem !important;
  }
  .me-sm-auto {
    margin-right: auto !important;
  }
  .text-sm-end {
    text-align: end !important;
  }
}
@media (min-width: 768px) {
  .p-md-0 {
    padding: 0 !important;
  }
  .p-md-1 {
    padding: 0.25rem !important;
  }
  .p-md-2 {
    padding: 0.5rem !important;
  }
  .p-md-3 {
    padding: 1rem !important;
  }
  .p-md-4 {
    padding: 1.5rem !important;
  }
  .p-md-5 {
    padding: 3rem !important;
  }
  .py-md-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-md-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-md-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-md-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-md-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-md-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-md-0 {
    padding-top: 0 !important;
  }
  .pt-md-1 {
    padding-top: 0.25rem !important;
  }
  .pt-md-2 {
    padding-top: 0.5rem !important;
  }
  .pt-md-3 {
    padding-top: 1rem !important;
  }
  .pt-md-4 {
    padding-top: 1.5rem !important;
  }
  .pt-md-5 {
    padding-top: 3rem !important;
  }
  .pb-md-0 {
    padding-bottom: 0 !important;
  }
  .pb-md-1 {
    padding-bottom: 0.25rem !important;
  }
  .pb-md-2 {
    padding-bottom: 0.5rem !important;
  }
  .pb-md-3 {
    padding-bottom: 1rem !important;
  }
  .pb-md-4 {
    padding-bottom: 1.5rem !important;
  }
  .pb-md-5 {
    padding-bottom: 3rem !important;
  }
  .px-md-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-md-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-md-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-md-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-md-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-md-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .ps-md-0 {
    padding-left: 0 !important;
  }
  .ps-md-1 {
    padding-left: 0.25rem !important;
  }
  .ps-md-2 {
    padding-left: 0.5rem !important;
  }
  .ps-md-3 {
    padding-left: 1rem !important;
  }
  .ps-md-4 {
    padding-left: 1.5rem !important;
  }
  .ps-md-5 {
    padding-left: 3rem !important;
  }
  .ms-md-0 {
    margin-left: 0 !important;
  }
  .ms-md-1 {
    margin-left: 0.25rem !important;
  }
  .ms-md-2 {
    margin-left: 0.5rem !important;
  }
  .ms-md-3 {
    margin-left: 1rem !important;
  }
  .ms-md-4 {
    margin-left: 1.5rem !important;
  }
  .ms-md-5 {
    margin-left: 3rem !important;
  }
  .ms-md-auto {
    margin-left: auto !important;
  }
  .me-md-0 {
    margin-right: 0 !important;
  }
  .me-md-1 {
    margin-right: 0.25rem !important;
  }
  .me-md-2 {
    margin-right: 0.5rem !important;
  }
  .me-md-3 {
    margin-right: 1rem !important;
  }
  .me-md-4 {
    margin-right: 1.5rem !important;
  }
  .me-md-5 {
    margin-right: 3rem !important;
  }
  .me-md-auto {
    margin-right: auto !important;
  }
  .text-md-end {
    text-align: end !important;
  }
}
@media (min-width: 992px) {
  .p-lg-0 {
    padding: 0 !important;
  }
  .p-lg-1 {
    padding: 0.25rem !important;
  }
  .p-lg-2 {
    padding: 0.5rem !important;
  }
  .p-lg-3 {
    padding: 1rem !important;
  }
  .p-lg-4 {
    padding: 1.5rem !important;
  }
  .p-lg-5 {
    padding: 3rem !important;
  }
  .py-lg-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-lg-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-lg-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-lg-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-lg-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-lg-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-lg-0 {
    padding-top: 0 !important;
  }
  .pt-lg-1 {
    padding-top: 0.25rem !important;
  }
  .pt-lg-2 {
    padding-top: 0.5rem !important;
  }
  .pt-lg-3 {
    padding-top: 1rem !important;
  }
  .pt-lg-4 {
    padding-top: 1.5rem !important;
  }
  .pt-lg-5 {
    padding-top: 3rem !important;
  }
  .pb-lg-0 {
    padding-bottom: 0 !important;
  }
  .pb-lg-1 {
    padding-bottom: 0.25rem !important;
  }
  .pb-lg-2 {
    padding-bottom: 0.5rem !important;
  }
  .pb-lg-3 {
    padding-bottom: 1rem !important;
  }
  .pb-lg-4 {
    padding-bottom: 1.5rem !important;
  }
  .pb-lg-5 {
    padding-bottom: 3rem !important;
  }
  .px-lg-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-lg-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-lg-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-lg-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-lg-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-lg-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .ps-lg-0 {
    padding-left: 0 !important;
  }
  .ps-lg-1 {
    padding-left: 0.25rem !important;
  }
  .ps-lg-2 {
    padding-left: 0.5rem !important;
  }
  .ps-lg-3 {
    padding-left: 1rem !important;
  }
  .ps-lg-4 {
    padding-left: 1.5rem !important;
  }
  .ps-lg-5 {
    padding-left: 3rem !important;
  }
  .ms-lg-0 {
    margin-left: 0 !important;
  }
  .ms-lg-1 {
    margin-left: 0.25rem !important;
  }
  .ms-lg-2 {
    margin-left: 0.5rem !important;
  }
  .ms-lg-3 {
    margin-left: 1rem !important;
  }
  .ms-lg-4 {
    margin-left: 1.5rem !important;
  }
  .ms-lg-5 {
    margin-left: 3rem !important;
  }
  .ms-lg-auto {
    margin-left: auto !important;
  }
  .me-lg-0 {
    margin-right: 0 !important;
  }
  .me-lg-1 {
    margin-right: 0.25rem !important;
  }
  .me-lg-2 {
    margin-right: 0.5rem !important;
  }
  .me-lg-3 {
    margin-right: 1rem !important;
  }
  .me-lg-4 {
    margin-right: 1.5rem !important;
  }
  .me-lg-5 {
    margin-right: 3rem !important;
  }
  .me-lg-auto {
    margin-right: auto !important;
  }
  .text-lg-end {
    text-align: end !important;
  }
}
@media (min-width: 1200px) {
  .p-xl-0 {
    padding: 0 !important;
  }
  .p-xl-1 {
    padding: 0.25rem !important;
  }
  .p-xl-2 {
    padding: 0.5rem !important;
  }
  .p-xl-3 {
    padding: 1rem !important;
  }
  .p-xl-4 {
    padding: 1.5rem !important;
  }
  .p-xl-5 {
    padding: 3rem !important;
  }
  .py-xl-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-xl-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-xl-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-xl-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-xl-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-xl-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-xl-0 {
    padding-top: 0 !important;
  }
  .pt-xl-1 {
    padding-top: 0.25rem !important;
  }
  .pt-xl-2 {
    padding-top: 0.5rem !important;
  }
  .pt-xl-3 {
    padding-top: 1rem !important;
  }
  .pt-xl-4 {
    padding-top: 1.5rem !important;
  }
  .pt-xl-5 {
    padding-top: 3rem !important;
  }
  .pb-xl-0 {
    padding-bottom: 0 !important;
  }
  .pb-xl-1 {
    padding-bottom: 0.25rem !important;
  }
  .pb-xl-2 {
    padding-bottom: 0.5rem !important;
  }
  .pb-xl-3 {
    padding-bottom: 1rem !important;
  }
  .pb-xl-4 {
    padding-bottom: 1.5rem !important;
  }
  .pb-xl-5 {
    padding-bottom: 3rem !important;
  }
  .px-xl-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-xl-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-xl-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-xl-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-xl-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-xl-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .ps-xl-0 {
    padding-left: 0 !important;
  }
  .ps-xl-1 {
    padding-left: 0.25rem !important;
  }
  .ps-xl-2 {
    padding-left: 0.5rem !important;
  }
  .ps-xl-3 {
    padding-left: 1rem !important;
  }
  .ps-xl-4 {
    padding-left: 1.5rem !important;
  }
  .ps-xl-5 {
    padding-left: 3rem !important;
  }
  .ms-xl-0 {
    margin-left: 0 !important;
  }
  .ms-xl-1 {
    margin-left: 0.25rem !important;
  }
  .ms-xl-2 {
    margin-left: 0.5rem !important;
  }
  .ms-xl-3 {
    margin-left: 1rem !important;
  }
  .ms-xl-4 {
    margin-left: 1.5rem !important;
  }
  .ms-xl-5 {
    margin-left: 3rem !important;
  }
  .ms-xl-auto {
    margin-left: auto !important;
  }
  .me-xl-0 {
    margin-right: 0 !important;
  }
  .me-xl-1 {
    margin-right: 0.25rem !important;
  }
  .me-xl-2 {
    margin-right: 0.5rem !important;
  }
  .me-xl-3 {
    margin-right: 1rem !important;
  }
  .me-xl-4 {
    margin-right: 1.5rem !important;
  }
  .me-xl-5 {
    margin-right: 3rem !important;
  }
  .me-xl-auto {
    margin-right: auto !important;
  }
  .text-xl-end {
    text-align: end !important;
  }
}
@media (min-width: 1400px) {
  .p-xxl-0 {
    padding: 0 !important;
  }
  .p-xxl-1 {
    padding: 0.25rem !important;
  }
  .p-xxl-2 {
    padding: 0.5rem !important;
  }
  .p-xxl-3 {
    padding: 1rem !important;
  }
  .p-xxl-4 {
    padding: 1.5rem !important;
  }
  .p-xxl-5 {
    padding: 3rem !important;
  }
  .py-xxl-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-xxl-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-xxl-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-xxl-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-xxl-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-xxl-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-xxl-0 {
    padding-top: 0 !important;
  }
  .pt-xxl-1 {
    padding-top: 0.25rem !important;
  }
  .pt-xxl-2 {
    padding-top: 0.5rem !important;
  }
  .pt-xxl-3 {
    padding-top: 1rem !important;
  }
  .pt-xxl-4 {
    padding-top: 1.5rem !important;
  }
  .pt-xxl-5 {
    padding-top: 3rem !important;
  }
  .pb-xxl-0 {
    padding-bottom: 0 !important;
  }
  .pb-xxl-1 {
    padding-bottom: 0.25rem !important;
  }
  .pb-xxl-2 {
    padding-bottom: 0.5rem !important;
  }
  .pb-xxl-3 {
    padding-bottom: 1rem !important;
  }
  .pb-xxl-4 {
    padding-bottom: 1.5rem !important;
  }
  .pb-xxl-5 {
    padding-bottom: 3rem !important;
  }
  .px-xxl-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-xxl-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-xxl-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-xxl-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-xxl-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-xxl-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .ps-xxl-0 {
    padding-left: 0 !important;
  }
  .ps-xxl-1 {
    padding-left: 0.25rem !important;
  }
  .ps-xxl-2 {
    padding-left: 0.5rem !important;
  }
  .ps-xxl-3 {
    padding-left: 1rem !important;
  }
  .ps-xxl-4 {
    padding-left: 1.5rem !important;
  }
  .ps-xxl-5 {
    padding-left: 3rem !important;
  }
  .ms-xxl-0 {
    margin-left: 0 !important;
  }
  .ms-xxl-1 {
    margin-left: 0.25rem !important;
  }
  .ms-xxl-2 {
    margin-left: 0.5rem !important;
  }
  .ms-xxl-3 {
    margin-left: 1rem !important;
  }
  .ms-xxl-4 {
    margin-left: 1.5rem !important;
  }
  .ms-xxl-5 {
    margin-left: 3rem !important;
  }
  .ms-xxl-auto {
    margin-left: auto !important;
  }
  .me-xxl-0 {
    margin-right: 0 !important;
  }
  .me-xxl-1 {
    margin-right: 0.25rem !important;
  }
  .me-xxl-2 {
    margin-right: 0.5rem !important;
  }
  .me-xxl-3 {
    margin-right: 1rem !important;
  }
  .me-xxl-4 {
    margin-right: 1.5rem !important;
  }
  .me-xxl-5 {
    margin-right: 3rem !important;
  }
  .me-xxl-auto {
    margin-right: auto !important;
  }
  .text-xxl-end {
    text-align: end !important;
  }
}
:host .dropdown-menu,
:host .dropdown-menu * {
  box-sizing: border-box;
}
:host .dropdown-menu[role=region]:focus {
  outline: none !important;
}
:host .dropdown-menu {
  --bsi-dropdown-item-padding-x: var(--bsi-spacing-xxs);
  --bsi-dropdown-item-padding-y: var(--bsi-spacing-xxs);
  --bsi-dropdown-spacer: 0;
  --bsi-dropdown-border-width: 1px;
  --bsi-dropdown-border-color: var(--bsi-color-border-subtle);
  left: 0 !important;
  width: 100%;
  background: var(--bsi-megamenu-background-color, #fff);
}
@media (min-width: 992px) {
  :host .dropdown-menu {
    --bsi-dropdown-padding-y: var(--bsi-spacing-m);
    --bsi-dropdown-padding-x: var(--bsi-spacing-xs);
  }
  :host .dropdown-menu .link-list-wrapper ul.link-list {
    display: grid;
    grid-auto-flow: column;
  }
}
:host .dropdown-menu .arrow {
  top: -10px !important;
  border-color: var(--bsi-dropdown-border-color);
  border-top-width: var(--bsi-dropdown-border-width);
  border-top-style: solid;
  border-left-width: var(--bsi-dropdown-border-width);
  border-left-style: solid;
}
:host it-popover .dropdown-menu.show {
  animation: none;
}`;

/* eslint-disable lit-a11y/list */
var ItMegamenu_1;
let ItMegamenu = ItMegamenu_1 = class ItMegamenu extends ItDropdownBase {
    constructor() {
        super(...arguments);
        this._hasDescription = false;
        this._hasHeader = false;
        this._hasFooter = false;
        // change default value for these properties
        this.fullWidth = true; // sovrascritto per settare un valore fisso e cambiarne il default
        this.active = false;
        this.columns = 2;
        this.footerAlign = 'left'; // allineamento del contento interno del footer
        this.footerPosition = 'bottom'; // posizione del footer
        this._clickCloseTargets = new Set();
        this._onTabKeyDown = (event, items, active) => {
            if (event.key === 'Tab') {
                // Usa la lista completa che include tutti gli elementi focusabili di header, i dropdown-item, e quelli di footer
                const _items = this._getMegamenuFocusableItems();
                const _currentIndex = _items.indexOf(active);
                if (event.shiftKey && _currentIndex === -1) {
                    // Shift+Tab dal primo elemento della lista completa: chiudi
                    this._popoverOpen = false;
                }
                if (!event.shiftKey && _currentIndex === _items.length - 1) {
                    // Tab dall'ultimo elemento della lista completa: chiudi
                    this._popoverOpen = false;
                }
                if (active.ariaDisabled) {
                    // as of the day of this implementation, tabbing through disabled items doesn't work natively
                    // maybe because of some web components behavior
                    if (event.shiftKey) {
                        this._ariaNav.handleKeyDown(new KeyboardEvent('keydown', { ...event, key: 'ArrowUp' }));
                    }
                    else {
                        this._ariaNav.handleKeyDown(new KeyboardEvent('keydown', { ...event, key: 'ArrowDown' }));
                    }
                }
            }
        };
        this._onInteractiveItemClick = () => {
            this._popoverOpen = false;
        };
    }
    _getAssignedSlotElements(slotName) {
        const slot = this.renderRoot.querySelector(`slot[name="${slotName}"]`);
        if (!slot)
            return [];
        return slot.assignedElements({ flatten: true }).filter((el) => el instanceof HTMLElement);
    }
    _refreshSlotState() {
        const descriptionSlot = this.renderRoot.querySelector('slot[name="description"]');
        const headerSlot = this.renderRoot.querySelector('slot[name="header"]');
        const footerSlot = this.renderRoot.querySelector('slot[name="footer"]');
        this._hasDescription = (descriptionSlot?.assignedElements({ flatten: true }).length ?? 0) > 0;
        this._hasHeader = (headerSlot?.assignedElements({ flatten: true }).length ?? 0) > 0;
        this._hasFooter = (footerSlot?.assignedElements({ flatten: true }).length ?? 0) > 0;
    }
    _handleSlotDescriptionChange() {
        // Defer al microtask successivo per evitare race di distribuzione slot su mobile.
        queueMicrotask(() => {
            this._refreshSlotState();
        });
    }
    _setChildrenProperties() {
        super._setChildrenProperties();
        // disable full-width for dropdown items inside megamenu to let them inherit the width of their container and not stretch to the full width of the megamenu
        for (const item of this._menuItems) {
            item.fullWidth = false;
        }
        this._syncClickCloseTargets();
    }
    _onSlotHeaderChange() {
        // Defer al microtask successivo per evitare race di distribuzione slot su mobile.
        queueMicrotask(() => {
            this._refreshSlotState();
            this._syncClickCloseTargets();
        });
    }
    _onSlotFooterChange() {
        // Defer al microtask successivo per evitare race di distribuzione slot su mobile.
        queueMicrotask(() => {
            this._refreshSlotState();
            this._syncClickCloseTargets();
        });
    }
    _getMegamenuFocusableItems() {
        // Costruisce la lista completa: header items + menu items + footer items
        const allItems = [];
        const headerItems = this._getAssignedSlotElements('header');
        const footerItems = this._getAssignedSlotElements('footer');
        // Aggiungi elementi focusabili dall'header
        for (const headerItem of headerItems) {
            if (headerItem.matches(ItMegamenu_1.FOCUSABLE_SELECTORS)) {
                allItems.push(headerItem);
            }
            allItems.push(...Array.from(headerItem.querySelectorAll(ItMegamenu_1.FOCUSABLE_SELECTORS)));
        }
        // Aggiungi menu items
        const menuItems = this._menuItems.map((item) => item.getFocusableElement()).filter((el) => !!el);
        allItems.push(...menuItems);
        // Aggiungi elementi focusabili dal footer
        for (const footerItem of footerItems) {
            if (footerItem.matches(ItMegamenu_1.FOCUSABLE_SELECTORS)) {
                allItems.push(footerItem);
            }
            allItems.push(...Array.from(footerItem.querySelectorAll(ItMegamenu_1.FOCUSABLE_SELECTORS)));
        }
        return allItems;
    }
    _syncClickCloseTargets() {
        const nextTargets = new Set();
        for (const item of this._getMegamenuFocusableItems()) {
            if (item instanceof HTMLElement) {
                nextTargets.add(item);
            }
        }
        for (const item of this._menuItems) {
            if (item instanceof HTMLElement) {
                nextTargets.add(item);
            }
        }
        for (const target of this._clickCloseTargets) {
            if (!nextTargets.has(target)) {
                target.removeEventListener('click', this._onInteractiveItemClick);
            }
        }
        for (const target of nextTargets) {
            if (!this._clickCloseTargets.has(target)) {
                target.addEventListener('click', this._onInteractiveItemClick);
            }
        }
        this._clickCloseTargets = nextTargets;
    }
    disconnectedCallback() {
        for (const target of this._clickCloseTargets) {
            target.removeEventListener('click', this._onInteractiveItemClick);
        }
        this._clickCloseTargets.clear();
        // eslint-disable-next-line wc/guard-super-call
        super.disconnectedCallback();
    }
    firstUpdated() {
        this._refreshSlotState();
        this._syncClickCloseTargets();
    }
    updated() {
        super.updated();
        this._refreshSlotState();
    }
    render() {
        return html `
      <it-popover
        placement=${this.alignment}
        @it-popover-open=${this._onPopoverOpen}
        @it-popover-close=${this._onPopoverClose}
        exportparts="focusable, icon, button, dropdown-button, dropdown-icon-expand, popover-content"
        part="dropdown-popover"
        ?open=${this._popoverOpen}
        offset="0"
        controlled
        ?no-flip=${true}
      >
        <it-button
          id=${this._buttonId}
          slot="trigger"
          ?disabled="${this.disabled}"
          type="button"
          variant=""
          size=${ifDefined(this.size)}
          @click=${this._onTriggerClick}
          @keydown=${{ handleEvent: this._onKeyDown, capture: true }}
          class="dropdown-toggle nav-link ${this.active ? 'active' : ''}"
          it-aria-label=${ifDefined(this.itAriaLabel ? this.itAriaLabel : undefined)}
          exportparts="focusable, button"
          part="dropdown-button"
          it-aria-haspopup="${this.itRole === 'list' ? 'true' : this.itRole}"
          it-aria-controls=${this._popoverOpen ? this._menuId : nothing}
        >
          ${this.alignment.startsWith('left')
            ? html `<it-icon
                name="it-expand"
                class="icon-expand left"
                size="xs"
                exportparts="icon"
                part="dropdown-icon-expand"
              ></it-icon>`
            : ''}
          ${this.label}
          ${!this.alignment.startsWith('left')
            ? html `<it-icon
                name="it-expand"
                class=${this.composeClass('icon-expand', {
                right: this.alignment.startsWith('right'),
                top: this.alignment.startsWith('top'),
            })}
                exportparts="icon"
                part="dropdown-icon-expand"
                size="xs"
              ></it-icon>`
            : ''}
        </it-button>
        <div
          slot="content"
          part="popover-content"
          class="${this.composeClass('dropdown-menu megamenu', 'shadow-lg', {
            show: this._popoverOpen,
            'full-width': this.fullWidth,
        })}"
          aria-labelledby=${this._buttonId}
          role="region"
          @keydown=${{ handleEvent: this._onKeyDown, capture: true }}
          id=${this._menuId}
          tabindex="-1"
        >
          <div class="megamenu-content" part="megamenu-content">
            <div class="row">
              ${this._hasDescription
            ? html `
                    <div class="col-xs-12 col-lg-4 px-0">
                      <div class="row">
                        <div class="col-12 it-vertical it-description pb-lg-3">
                          <div class="description-content px-3 px-lg-4 ps-sm-5 ms-lg-3">
                            <slot name="description" @slotchange=${this._handleSlotDescriptionChange}></slot>
                          </div>
                        </div>
                      </div>
                    </div>
                  `
            : html ` <slot name="description" @slotchange=${this._handleSlotDescriptionChange}></slot> `}
              <div
                class="${this.composeClass('col-12', {
            'col-lg-8': this._hasDescription || this.footerPosition === 'right',
        })}"
              >
                <!-- HEADER PLACEHOLDER -->

                ${this._hasHeader
            ? html `
                      <div class="it-heading-link-wrapper" part="megamenu-header">
                        <slot name="header" @slotchange=${this._onSlotHeaderChange}></slot>
                      </div>
                    `
            : html `<slot name="header" @slotchange=${this._onSlotHeaderChange}></slot>`}

                <!-- LINKS -->
                ${this._menuItems.length > 0
            ? html ` <div class="row">
                      <div class="col-12">
                        <div class="link-list-wrapper" part="megamenu-link-list-wrapper">
                          <ul
                            class="link-list"
                            part="megamenu-link-list"
                            style="grid-template-rows: repeat(${Math.ceil(this._menuItems.length / this.columns)}, auto);"
                            role=${ifDefined(this.itRole !== 'list' ? this.itRole : undefined)}
                          >
                            <slot @slotchange=${this._setChildrenProperties}></slot>
                          </ul>
                        </div>
                      </div>
                    </div>`
            : html `<slot @slotchange=${this._setChildrenProperties}></slot>`}
              </div>

              <!-- FOOTER -->
              <div
                class="${this.composeClass('col-12', {
            'col-lg-4 px-0': this.footerPosition === 'right',
        })}"
              >
                ${this._hasFooter
            ? html `
                      <div
                        class="${this.composeClass('', {
                'text-end': this.footerAlign === 'right',
                'it-footer-link-wrapper': this.footerPosition !== 'right',
                'it-footer-link-wrapper-vertical': this.footerPosition === 'right',
            })}"
                        part="megamenu-footer"
                      >
                        <slot name="footer" @slotchange=${this._onSlotFooterChange}></slot>
                      </div>
                    `
            : html `<slot name="footer" @slotchange=${this._onSlotFooterChange}></slot>`}
              </div>
            </div>
          </div>
        </div>
      </it-popover>
    `;
    }
};
ItMegamenu.styles = [ItDropdownBase.styles, styles];
ItMegamenu.FOCUSABLE_SELECTORS = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
__decorate$1([
    state(),
    __metadata$1("design:type", Object)
], ItMegamenu.prototype, "_hasDescription", void 0);
__decorate$1([
    state(),
    __metadata$1("design:type", Object)
], ItMegamenu.prototype, "_hasHeader", void 0);
__decorate$1([
    state(),
    __metadata$1("design:type", Object)
], ItMegamenu.prototype, "_hasFooter", void 0);
__decorate$1([
    property({ type: Boolean, attribute: 'full-width', reflect: true }),
    __metadata$1("design:type", Object)
], ItMegamenu.prototype, "fullWidth", void 0);
__decorate$1([
    property({ type: Boolean, reflect: true }),
    __metadata$1("design:type", Object)
], ItMegamenu.prototype, "active", void 0);
__decorate$1([
    property({ type: Number, attribute: 'columns', reflect: true }),
    __metadata$1("design:type", Number)
], ItMegamenu.prototype, "columns", void 0);
__decorate$1([
    property({ type: String, attribute: 'footer-align', reflect: true }),
    __metadata$1("design:type", String)
], ItMegamenu.prototype, "footerAlign", void 0);
__decorate$1([
    property({ type: String, attribute: 'footer-position', reflect: true }),
    __metadata$1("design:type", String)
], ItMegamenu.prototype, "footerPosition", void 0);
ItMegamenu = ItMegamenu_1 = __decorate$1([
    customElement('it-megamenu')
], ItMegamenu);

export { ItMegamenu };
//# sourceMappingURL=it-megamenu.js.map
